/*
 * «kubectl» кластера: kubeconfig для входа под своим логином Keycloak (Pinniped Concierge,
 * бэкенд-плагин viva-kubectl). Файл без секретов — при первом запросе kubectl pinniped откроет
 * браузер на вход в Keycloak; права в кластере — политики k8s.* (роли и группы Keycloak).
 */
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CodeSnippet, InfoCard, Link, Progress } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';

const useStyles = makeStyles(theme => ({
  muted: { color: theme.palette.text.secondary },
}));

export const ClusterKubectlCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const cluster = entity.metadata.name;
  const file = `${cluster}.kubeconfig`;

  const state = useAsync(async (): Promise<string | undefined> => {
    const base = await discoveryApi.getBaseUrl('viva-kubectl');
    const res = await fetchApi.fetch(`${base}/clusters/${encodeURIComponent(cluster)}/kubeconfig`);
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error(`kubeconfig: HTTP ${res.status}`);
    return res.text();
  }, [cluster]);

  const download = (text: string) => {
    const url = URL.createObjectURL(new Blob([text], { type: 'application/yaml' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = file;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <InfoCard title="kubectl">
      {state.loading && <Progress />}
      {state.error && (
        <Typography variant="body2" color="error">
          kubeconfig не получен: {state.error.message}
        </Typography>
      )}
      {!state.loading && !state.error && !state.value && (
        <Typography variant="body2" className={classes.muted}>
          Вход в kubectl через Keycloak у этого кластера не настроен: его включает platform:bootstrap,
          когда у установки есть Keycloak.
        </Typography>
      )}
      {state.value && (
        <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
          <Typography variant="body2">
            kubectl под своим логином: при первом запросе откроется вход в Keycloak. Права — по политикам
            k8s.* ваших ролей: k8s.view — просмотр, k8s.edit — изменение в namespace сервисов команды,
            k8s.admin — весь кластер.
          </Typography>
          <Box>
            <Button variant="outlined" color="primary" startIcon={<GetAppIcon />} onClick={() => download(state.value!)}>
              Скачать {file}
            </Button>
          </Box>
          <Typography variant="body2">
            Нужны kubectl и{' '}
            <Link to="https://pinniped.dev/docs/howto/install-cli/" target="_blank" rel="noopener">
              pinniped CLI
            </Link>
            :
          </Typography>
          <CodeSnippet
            language="bash"
            text={`brew install vmware/pinniped/pinniped-cli\nexport KUBECONFIG=~/Downloads/${file}\nkubectl get namespaces`}
            showCopyCodeButton
          />
        </Box>
      )}
    </InfoCard>
  );
};
